import { type ReactNode, useState } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  CountPill,
  HorizontalMenuTabs,
  PageHeader,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type CoachingResponse,
  type CoachRow,
  type CoachTypeSummary,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, Link, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';

import { percentOf } from './analytics.labels';
import { mutedTextSx, selectableRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { useAgentContext } from './CoachingCards.agentContext';
import {
  type ActivityLine,
  ActivityLegend,
  ActivityLineChart,
  RecencyDonut,
} from './EngagementCards';
import { useStudentData } from './useStudentData';
import { COLUMNS, COMMON_COLUMNS, singleCoachColumn } from './viewData.columns';

const { coaching, hover } = appStrings.modules.coachingAnalytics;

const COACHING_COLUMNS = [...COMMON_COLUMNS, COLUMNS.coach, COLUMNS.coachType];

type CoachType = CoachRow['type'];

const COACH_TYPE_COLORS: Readonly<Record<CoachType, ColorToken>> = {
  backrs: 'Blue/500',
  institutional: 'Green/500',
};

const LOAD_ROWS_SHOWN = 10;

const COACH_TABS: readonly { type: CoachType; label: string; coachLabel: string }[] = [
  {
    type: 'institutional',
    label: coaching.load.institutional,
    coachLabel: coaching.load.institutionalCoach,
  },
  { type: 'backrs', label: coaching.load.backrs, coachLabel: coaching.load.backrsCoach },
];

const coachName = (coach: CoachRow): string =>
  `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim();

const CoachingLoadRowItem = ({
  row,
  max,
  cohortTotal,
  onSelect,
}: Readonly<{ row: CoachRow; max: number; cohortTotal: number; onSelect: () => void }>) => {
  const pct = max > 0 ? Math.min((row.caseload / max) * 100, 100) : 0;

  return (
    <Tooltip
      arrow
      placement="top"
      title={hover.share(coachName(row), row.caseload, percentOf(row.caseload, cohortTotal))}
    >
      <Box
        id={`coaching-analytics-coach-row-${row.id}`}
        sx={[loadRowSx, selectableRowSx]}
        onClick={onSelect}
      >
        <Typography variant="text-sm-medium" sx={loadNameSx}>
          {coachName(row)}
        </Typography>
        <Box sx={loadTrackSx}>
          <Box
            sx={loadFillSx}
            style={{ width: `${pct}%`, backgroundColor: COLOR_TOKENS[COACH_TYPE_COLORS[row.type]] }}
          />
        </Box>
        <CountPill value={row.caseload} sx={loadValueSx} />
      </Box>
    </Tooltip>
  );
};

const HeadlineMetric = ({ label, value }: Readonly<{ label: string; value: number }>) => (
  <Box sx={headlineSx}>
    <Typography variant="display-sm-semibold" sx={headlineValueSx}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="text-sm-regular" sx={mutedTextSx}>
      {label}
    </Typography>
  </Box>
);

const CoachingLoadCard = ({ data }: Readonly<{ data: CoachingResponse }>) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const tab = COACH_TABS[tabIndex];
  const summary: CoachTypeSummary = data.by_type[tab.type];
  const coaches = [...summary.coaches].sort((a, b) => b.caseload - a.caseload);
  const maxCaseload = Math.max(...coaches.map((coach) => coach.caseload), 0);
  const shown = showAll ? coaches : coaches.slice(0, LOAD_ROWS_SHOWN);

  const students = useStudentData(
    'coaching-analytics-card-coaching-load',
    coaching.load.title,
    COACHING_COLUMNS,
  );

  return (
    <AnalyticsCard
      title={coaching.load.title}
      subtitle={coaching.load.subtitle}
      info={[coaching.load.info]}
      viewData={students}
      belowHeader={
        <Box sx={tabsRowSx}>
          <HorizontalMenuTabs
            id="coaching-analytics-coach-type-tabs"
            variant="filter"
            tabs={COACH_TABS.map((option) => option.label)}
            value={tabIndex}
            setValue={(next) => {
              setTabIndex(next);
              setShowAll(false);
            }}
          />
        </Box>
      }
    >
      <Box sx={headlineRowSx}>
        <HeadlineMetric
          label={coaching.load.avgStudentsPerCoach}
          value={Math.round(summary.average_per_coach)}
        />
        <HeadlineMetric
          label={coaching.load.studentsWithCoach(tab.coachLabel)}
          value={summary.students_with_coach}
        />
      </Box>
      {coaches.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {coaching.load.empty}
        </Typography>
      ) : (
        <Box sx={loadListSx}>
          {shown.map((row) => (
            <CoachingLoadRowItem
              key={row.id}
              row={row}
              max={maxCaseload}
              cohortTotal={data.coverage.students}
              onSelect={() =>
                students.openSegment({
                  label: coachName(row),
                  where: [{ field: 'coach_ids', op: 'eq', value: row.id }],
                  columns: [...COMMON_COLUMNS, singleCoachColumn(coachName(row))],
                })
              }
            />
          ))}
          {coaches.length > LOAD_ROWS_SHOWN && (
            <Box sx={viewAllRowSx}>
              <Link
                id={`coaching-analytics-coaching-load-view-${showAll ? 'less' : 'all'}`}
                component="button"
                variant="text-sm-semibold"
                underline="none"
                sx={viewAllSx}
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? coaching.load.viewLess : coaching.load.viewAll}
              </Link>
            </Box>
          )}
        </Box>
      )}
    </AnalyticsCard>
  );
};

const CoachRecencyCard = ({ data }: Readonly<{ data: CoachingResponse }>) => (
  <AnalyticsCard
    title={coaching.coachRecency.title}
    subtitle={coaching.coachRecency.subtitle}
    info={[coaching.coachRecency.info]}
    id="coaching-analytics-card-coach-recency"
  >
    <RecencyDonut
      recency={data.institutional_coaches.recently_active}
      idPrefix="coaching-analytics-coach-recency-legend"
    />
  </AnalyticsCard>
);

const COACH_ACTIVITY_SERIES: readonly ActivityLine[] = [
  { key: 'posts', label: coaching.coachActivity.posts, color: 'Brand/800' },
  { key: 'comments', label: coaching.coachActivity.comments, color: 'Brand/300' },
];

const CoachActivityCard = ({ data }: Readonly<{ data: CoachingResponse }>) => {
  const months = data.institutional_coaches.activity.monthly.series;

  return (
    <AnalyticsCard
      title={coaching.coachActivity.title}
      subtitle={coaching.coachActivity.subtitle(months.length)}
      info={[coaching.coachActivity.info]}
      id="coaching-analytics-card-coach-activity"
      belowHeader={<ActivityLegend lines={COACH_ACTIVITY_SERIES} />}
    >
      <ActivityLineChart
        months={months}
        lines={COACH_ACTIVITY_SERIES}
        ariaLabel={coaching.coachActivity.title}
        hoverText={(month, entry) =>
          coaching.coachActivity.month(month, entry.posts, entry.comments)
        }
      />
    </AnalyticsCard>
  );
};

const CoachingDetail = ({ data }: Readonly<{ data: CoachingResponse }>) => (
  <Box sx={detailGridSx}>
    <CoachRecencyCard data={data} />
    <CoachActivityCard data={data} />
  </Box>
);

export const CoachingSection = ({
  data,
  action,
  detail = false,
}: Readonly<{ data: CoachingResponse; action?: ReactNode; detail?: boolean }>) => {
  useAgentContext(data, detail);

  return (
    <Box sx={sectionSx}>
      <PageHeader
        title={coaching.title}
        subtitle={coaching.subtitle}
        maxWidth={null}
        rightComponents={[
          action ?? (
            <LubavButton
              key="coaching-discuss-lubav"
              id="coaching-analytics-coaching-discuss-lubav"
              label={coaching.discussWithLubav}
              promptButtonId="coaching_analytics_coaching"
            />
          ),
        ]}
      />
      <CoachingLoadCard data={data} />
      {detail && <CoachingDetail data={data} />}
    </Box>
  );
};

const detailGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const viewAllRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};

const viewAllSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  '&:hover': { textDecoration: 'underline' },
});

const tabsRowSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
};

const headlineRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

const headlineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const headlineValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const loadListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const loadRowSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const loadNameSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
  minWidth: 148,
  flexShrink: 0,
});

const loadTrackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  height: 12,
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  overflow: 'hidden',
});

const loadFillSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  borderRadius: backrs.radius['radius-full'],
});

const loadValueSx: SxProps<Theme> = {
  minWidth: 24,
  flexShrink: 0,
};
